import { useState, useCallback } from 'react';

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  type: 'warning' | 'danger' | 'success' | 'info';
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  loading: boolean;
}

interface ToastState {
  isOpen: boolean;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  duration: number;
}

export const useDialog = () => {
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    confirmText: '确认',
    cancelText: '取消',
    onConfirm: () => {},
    loading: false
  });

  const [toast, setToast] = useState<ToastState>({
    isOpen: false,
    message: '',
    type: 'info',
    duration: 4000
  });

  // 显示确认对话框
  const showConfirm = useCallback((options: {
    title: string;
    message: string;
    type?: 'warning' | 'danger' | 'success' | 'info';
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void | Promise<void>;
  }) => {
    setConfirmDialog({
      isOpen: true,
      title: options.title,
      message: options.message,
      type: options.type || 'warning',
      confirmText: options.confirmText || '确认',
      cancelText: options.cancelText || '取消',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, loading: true }));
        try {
          await options.onConfirm();
          setConfirmDialog(prev => ({ ...prev, isOpen: false, loading: false }));
        } catch (error) {
          setConfirmDialog(prev => ({ ...prev, loading: false }));
          console.error('确认操作失败:', error);
        }
      },
      loading: false
    });
  }, []);

  // 关闭确认对话框
  const closeConfirm = useCallback(() => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false, loading: false }));
  }, []);

  // 显示通知
  const showToast = useCallback((options: {
    message: string;
    type?: 'success' | 'error' | 'warning' | 'info';
    title?: string;
    duration?: number;
  }) => {
    setToast({
      isOpen: true,
      message: options.message,
      type: options.type || 'info',
      title: options.title,
      duration: options.duration || 4000
    });
  }, []);

  // 关闭通知
  const closeToast = useCallback(() => {
    setToast(prev => ({ ...prev, isOpen: false }));
  }, []);

  // 便捷方法
  const showSuccess = useCallback((message: string, title?: string) => {
    showToast({ message, type: 'success', title });
  }, [showToast]);

  const showError = useCallback((message: string, title?: string) => {
    showToast({ message, type: 'error', title });
  }, [showToast]);

  const showWarning = useCallback((message: string, title?: string) => {
    showToast({ message, type: 'warning', title });
  }, [showToast]);

  const showInfo = useCallback((message: string, title?: string) => {
    showToast({ message, type: 'info', title });
  }, [showToast]);

  return {
    // 确认对话框
    confirmDialog,
    showConfirm,
    closeConfirm,
    
    // 通知
    toast,
    showToast,
    closeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
};

export default useDialog;